import { Product, ProductTenor } from '@protos/product';
import { Side } from '@protos/trading';

export interface LadderPayload {
  tabs: StoredLadderDef[];
  lastVisibleTab: number;
}

export interface StoredLadderDef {
  symbol: string | null;
  tenorCode: string | null;
  exchange: string | null;
}

export interface LadderInfo {
  index: number;
  product: Product | null;
  tenor: ProductTenor | null;
  exchange: string | null;
  label: string;
  config: LadderTabConfig;
}

export interface LadderEntry {
  price: number;
  quantity: number;
  side: Side;
  hasBid: boolean;
  hasAsk: boolean;
  isFairValue: boolean;
  expansionTicks: number;
}

export interface LadderSpread {
  bid: {
    min: number;
    max: number;
  };
  ask: {
    min: number;
    max: number;
  };
  hasAnyBids: boolean;
  hasAnyAsks: boolean;
}

export interface LiveLadderData {
  index: number;
  lastUpdateTime: number;
  ladder: LadderEntry[];
  total: number;
  min: number;
  max: number;
  fairValueIndex: number;
  lastMaxValues: number[];
  currentSpread?: LadderSpread;
  historicalSpread?: LadderSpread;
}

export enum LadderBarScalingMode {
  LatestOnly = 'LATEST',
  LastFive = 'LAST_FIVE',
  LastTen = 'LAST_TEN',
}

export interface LadderConfig {
  barScaling: LadderBarScalingMode;
  showAllTicks: boolean;
}

export interface LadderTabConfig {
  showAllTicks: boolean;
  allowFillOrKill: boolean;
}

// Utils
const easeInOutQuad = x => {
  return x < 0.5 ? 2 * x * x : 1 - Math.pow(-2 * x + 2, 2) / 2;
};

export const isLadderInfoEmpty = (tab: { product: Product | null; tenor: ProductTenor | null } | undefined) => {
  if (!tab) return true;
  return tab.product === null || tab.tenor === null;
};

//
export const getDummyLadder = (numRows: number = 30): Pick<LiveLadderData, 'ladder' | 'total' | 'min' | 'max' | 'fairValueIndex'> => {
  const ladder: LadderEntry[] = [];
  const start = Math.floor(Math.random() * 30 + 50);
  const delta = 0.01;
  const range = numRows * 0.5 * delta;
  const mid = start + range;
  const fairValueIndex = Math.floor(numRows / 2);
  const maxNumOrders = Math.random() * 1000 + 400;
  const maxVariance = maxNumOrders * 0.1;

  for (let i = 0; i < numRows; i++) {
    const price = start + i * 0.01;
    const normalisedDistanceToMid = Math.abs(mid - price) / range;
    const quantity = Math.round(easeInOutQuad(1.0 - normalisedDistanceToMid) * maxNumOrders + Math.random() * maxVariance);

    ladder.push({
      price: start + i * 0.01,
      quantity,
      hasBid: false,
      hasAsk: false,
      side: Side.UNSPECIFIED,
      isFairValue: i === fairValueIndex,
      expansionTicks: 0,
    });
  }
  ladder.sort((a, b) => (a.price > b.price ? -1 : 1));

  const total = ladder.reduce((prev, current) => prev + current.quantity, 0);
  const max = ladder.reduce((prev, current) => Math.max(prev, current.quantity), 0);
  const min = ladder.reduce((prev, current) => Math.min(prev, current.quantity), 99999);

  return { ladder, total, min, max, fairValueIndex };
};

export const emptyLadderDef: StoredLadderDef = { symbol: null, tenorCode: null, exchange: null };

export const storedLadderDefToLadderInfo = (ladderDef: StoredLadderDef, products: Product[], tenors: ProductTenor[], index: number): LadderInfo => {
  const { symbol, tenorCode, exchange } = ladderDef;
  const product = products.find(p => p.symbol === symbol) || null;
  const tenor = tenors.find(t => t.code === tenorCode) || null;
  const label = getLabelForTab({ product, tenor, exchange, index });

  return {
    product,
    tenor,
    exchange,
    index,
    label,
    config: {
      showAllTicks: false,
      allowFillOrKill: true,
    },
  };
};

export const getLabelForTab = (tab: { product: Product | null; tenor: ProductTenor | null; exchange: string | null; index: number }) => {
  if (!tab.product || !tab.tenor) {
    return 'New Tab ' + (tab.index + 1);
  }
  return `${tab.product.symbol} ${tab.tenor.display} (${tab.exchange || ''})`.toUpperCase();
};

export const makeDummyLiveLadderData = (tab: LadderInfo): LiveLadderData => {
  return {
    index: tab.index,
    lastUpdateTime: new Date().getTime(),
    ...getDummyLadder(20),
    lastMaxValues: [],
  };
};
